import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatarParaMoedaBRL } from "../../../utils";

interface PendenciaScoreProps {
  gps: any;
  creditoScore: any;
}

export const PendenciaScore = ({ gps, creditoScore }: PendenciaScoreProps) => {
  if (!gps && !creditoScore) return null;

  const parseValor = (v: any): number => {
    if (typeof v === "number") return v;
    if (typeof v === "string") {
      const clean = v.replace("R$", "").replace(/\./g, "").replace(",", ".").trim();
      return parseFloat(clean) || 0;
    }
    return 0;
  };

  const pendFinanceiras = gps?.PEND_FINANCEIRAS || creditoScore?.PEND_FINANCEIRAS;
  const protestos = gps?.PROTESTOS || creditoScore?.PROTESTOS;
  const chSemFundos = gps?.CH_SEM_FUNDOS_BACEN;
  const pendRefin = creditoScore?.PEND_REFIN;
  const pendVencidas = creditoScore?.PEND_VENCIDAS;
  const scores = creditoScore?.SCORES;

  const resumoItems = [
    { label: "PEND. FINANCEIRAS", count: Number(pendFinanceiras?.QUANTIDADE_OCORRENCIA || 0) },
    { label: "REFIN", count: Number(pendRefin?.QUANTIDADE_OCORRENCIA || 0) },
    { label: "PEND. VENCIDAS", count: Number(pendVencidas?.QUANTIDADE_OCORRENCIA || 0) },
    { label: "PROTESTOS", count: Number(protestos?.QUANTIDADE_OCORRENCIA || 0) },
    { label: "CHEQUES S/ FUNDOS", count: Number(chSemFundos?.QUANTIDADE_OCORRENCIA || 0) },
  ];

  const getScoreConfig = (score: number) => {
    if (score >= 850) return { label: "Excelente", color: "#10B981", bgColor: "#F0FDF4", textColor: "#03543F", borderColor: "#DCFCE7" };
    if (score >= 700) return { label: "Bom", color: "#3B82F6", bgColor: "#F0F9FF", textColor: "#0369A1", borderColor: "#E0F2FE" };
    if (score >= 500) return { label: "Regular", color: "#F59E0B", bgColor: "#FEFBF0", textColor: "#92400E", borderColor: "#FEF3C7" };
    return { label: "Ruim", color: "#EF4444", bgColor: "#FDF2F2", textColor: "#9B1C1C", borderColor: "#FDE8E8" };
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    border: "1px solid #E5E7EB",
    boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
    padding: "24px",
  };

  const sectionHeader = (title: string, badge?: React.ReactNode) => (
    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
      <h3 style={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4B5563", margin: 0, whiteSpace: "nowrap" }}>
        {title}
      </h3>
      <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }} />
      {badge}
    </div>
  );

  const qtdBadge = (count: number) => (
    <span style={{
      backgroundColor: count > 0 ? "#FDE8E8" : "#DEF7EC",
      color: count > 0 ? "#9B1C1C" : "#03543F",
      fontSize: "0.75rem",
      fontWeight: 700,
      padding: "2px 10px",
      borderRadius: "4px",
      whiteSpace: "nowrap",
    }}>
      {count > 0 ? `${count} ocorrência${count !== 1 ? "s" : ""}` : "Nada Consta"}
    </span>
  );

  const nadaConstaBlock = () => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "16px", backgroundColor: "#F0FDF4", borderRadius: "8px", color: "#16A34A", fontWeight: 600 }}>
      <span className="pi pi-check-circle" style={{ fontSize: "1.25rem" }} />
      Nada Consta
    </div>
  );

  const renderPendenciasTable = (ocorrencias: any[]) => (
    <DataTable size="small" value={ocorrencias} emptyMessage="Nenhuma ocorrência encontrada." style={{ borderRadius: "8px", overflow: "hidden" }}>
      <Column field="DATA_INCLUSAO" header="INCLUSÃO" />
      <Column field="DATA_VENCIMENTO" header="VENCIMENTO" />
      <Column field="CONTRATO" header="CONTRATO" />
      <Column field="CREDOR" header="CREDOR" />
      <Column field="VALOR" header="VALOR" body={(r: any) => formatarParaMoedaBRL(parseValor(r.VALOR))} />
    </DataTable>
  );

  const renderProtestosTable = (ocorrencias: any[]) => (
    <DataTable size="small" value={ocorrencias} emptyMessage="Nenhuma ocorrência encontrada." style={{ borderRadius: "8px", overflow: "hidden" }}>
      <Column field="DATA" header="DATA" />
      <Column field="VALOR" header="VALOR" body={(r: any) => formatarParaMoedaBRL(parseValor(r.VALOR))} />
      <Column field="CREDOR" header="CREDOR" />
      <Column field="CARTORIO" header="CARTÓRIO" />
      <Column field="TIPO_ANOTACAO" header="TIPO" body={(r: any) => r.TIPO_ANOTACAO || "NÃO DIVULGADO"} />
    </DataTable>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", color: "#1F2937" }}>

      {/* Resumo */}
      <div style={cardStyle}>
        {sectionHeader("Resumo da Consulta")}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px" }}>
          {resumoItems.map((item) => (
            <div key={item.label} style={{
              border: `1px solid ${item.count > 0 ? "#FEE2E2" : "#BBF7D0"}`,
              borderRadius: "12px",
              padding: "16px",
              backgroundColor: item.count > 0 ? "#FFF5F5" : "#F0FDF4",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}>
              <span className={item.count > 0 ? "pi pi-exclamation-triangle" : "pi pi-check-circle"}
                style={{ color: item.count > 0 ? "#EF4444" : "#16A34A", fontSize: "1.1rem" }} />
              <span style={{ fontSize: "1.75rem", fontWeight: 800, color: item.count > 0 ? "#EF4444" : "#16A34A", lineHeight: 1 }}>
                {item.count}
              </span>
              <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: "#6B7280", letterSpacing: "0.04em", lineHeight: "1.3" }}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Score de Crédito */}
      {scores?.OCORRENCIAS?.length > 0 && (
        <div style={cardStyle}>
          {sectionHeader("Score de Crédito")}
          <p style={{ color: "#6B7280", margin: "0 0 20px 0", lineHeight: "1.6", fontSize: "small" }}>
            Mede a probabilidade de inadimplência com base no histórico e perfil de crédito do consultado.
          </p>
          {scores.OCORRENCIAS.map((item: any, index: number) => {
            const scoreValue = Number(item.SCORE) || 0;
            const config = getScoreConfig(scoreValue);
            const positionPercent = Math.min(100, (scoreValue / 1000) * 100);
            return (
              <div key={index} style={{ marginBottom: index < scores.OCORRENCIAS.length - 1 ? "32px" : 0, paddingBottom: index < scores.OCORRENCIAS.length - 1 ? "32px" : 0, borderBottom: index < scores.OCORRENCIAS.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                {item.TIPO_SCORE && (
                  <div style={{ fontSize: "0.875rem", fontWeight: 700, color: "#374151", marginBottom: "12px" }}>
                    {item.TIPO_SCORE}
                  </div>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", alignItems: "center" }}>
                  <div style={{ flex: "1.2 1 280px" }}>
                    <div style={{ position: "relative", width: "100%", height: "14px", borderRadius: "7px", background: "linear-gradient(to right, #EF4444 0%, #EF4444 50%, #F59E0B 50%, #F59E0B 70%, #3B82F6 70%, #3B82F6 85%, #10B981 85%, #10B981 100%)", marginTop: "36px", marginBottom: "8px" }}>
                      <div style={{ position: "absolute", left: `${positionPercent}%`, bottom: "100%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "4px" }}>
                        <div style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: "4px", padding: "2px 6px", fontSize: "0.75rem", fontWeight: 800, color: "#374151", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", whiteSpace: "nowrap" }}>
                          {scoreValue}/1000
                        </div>
                        <svg width="10" height="6" viewBox="0 0 10 6" style={{ display: "block", marginTop: "-1px" }}>
                          <path d="M0 0 L5 6 L10 0 Z" fill={config.color} />
                        </svg>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "#9CA3AF", fontWeight: 600, position: "relative" }}>
                      <span>0</span>
                      <span style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>500</span>
                      <span>1000</span>
                    </div>
                  </div>
                  <div style={{ flex: "1 1 230px", backgroundColor: config.bgColor, border: `1px solid ${config.borderColor}`, borderRadius: "12px", padding: "16px 20px", display: "flex", gap: "12px" }}>
                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: config.color, flexShrink: 0, marginTop: "5px" }} />
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <span style={{ fontWeight: 700, color: config.textColor }}>{config.label} — {scoreValue}/1000</span>
                      {item.PROBABILIDADE_INADIMPLENCIA && (
                        <span style={{ fontSize: "small", color: config.textColor }}>
                          Prob. inadimplência: <strong>{item.PROBABILIDADE_INADIMPLENCIA}</strong>
                        </span>
                      )}
                      {item.TEXTO && (
                        <span style={{ fontSize: "small", color: "#6B7280", lineHeight: "1.4", marginTop: "4px" }}>{item.TEXTO}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pendências Financeiras */}
      {pendFinanceiras && (
        <div style={cardStyle}>
          {sectionHeader("Pendências Financeiras", qtdBadge(Number(pendFinanceiras.QUANTIDADE_OCORRENCIA || 0)))}
          {!pendFinanceiras.OCORRENCIAS || pendFinanceiras.OCORRENCIAS.length === 0
            ? nadaConstaBlock()
            : renderPendenciasTable(pendFinanceiras.OCORRENCIAS)}
        </div>
      )}

      {/* REFIN */}
      {pendRefin && (
        <div style={cardStyle}>
          {sectionHeader("Pendências REFIN", qtdBadge(Number(pendRefin.QUANTIDADE_OCORRENCIA || 0)))}
          {!pendRefin.OCORRENCIAS || pendRefin.OCORRENCIAS.length === 0
            ? nadaConstaBlock()
            : renderPendenciasTable(pendRefin.OCORRENCIAS)}
        </div>
      )}

      {/* Pendências Vencidas */}
      {pendVencidas && (
        <div style={cardStyle}>
          {sectionHeader("Pendências Vencidas", qtdBadge(Number(pendVencidas.QUANTIDADE_OCORRENCIA || 0)))}
          {!pendVencidas.OCORRENCIAS || pendVencidas.OCORRENCIAS.length === 0
            ? nadaConstaBlock()
            : renderPendenciasTable(pendVencidas.OCORRENCIAS)}
        </div>
      )}

      {/* Protestos */}
      {protestos && (
        <div style={cardStyle}>
          {sectionHeader("Protestos", qtdBadge(Number(protestos.QUANTIDADE_OCORRENCIA || 0)))}
          {!protestos.OCORRENCIAS || protestos.OCORRENCIAS.length === 0
            ? nadaConstaBlock()
            : renderProtestosTable(protestos.OCORRENCIAS)}
        </div>
      )}

      {/* Cheques sem Fundos BACEN */}
      {chSemFundos && (
        <div style={cardStyle}>
          {sectionHeader("Cheques sem Fundos (BACEN)", qtdBadge(Number(chSemFundos.QUANTIDADE_OCORRENCIA || 0)))}
          {Number(chSemFundos.QUANTIDADE_OCORRENCIA || 0) === 0 ? (
            nadaConstaBlock()
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ fontSize: "0.75rem", color: "#9CA3AF", textTransform: "uppercase", fontWeight: 600 }}>Quantidade</span>
                <span style={{ fontWeight: 700, color: "#EF4444", fontSize: "1.25rem" }}>{chSemFundos.QUANTIDADE_OCORRENCIA}</span>
              </div>
              {chSemFundos.CORRENTISTA && (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <span style={{ fontSize: "0.75rem", color: "#9CA3AF", textTransform: "uppercase", fontWeight: 600 }}>Correntista</span>
                  <span style={{ fontWeight: 600 }}>{chSemFundos.CORRENTISTA}</span>
                </div>
              )}
              {chSemFundos.CPFCNPJ && (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <span style={{ fontSize: "0.75rem", color: "#9CA3AF", textTransform: "uppercase", fontWeight: 600 }}>CPF/CNPJ</span>
                  <span style={{ fontWeight: 600 }}>{chSemFundos.CPFCNPJ}</span>
                </div>
              )}
              {chSemFundos.TIPO_PESSOA && (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <span style={{ fontSize: "0.75rem", color: "#9CA3AF", textTransform: "uppercase", fontWeight: 600 }}>Tipo de Pessoa</span>
                  <span style={{ fontWeight: 600 }}>{chSemFundos.TIPO_PESSOA}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
};
